import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, Users, Award, ChevronRight, X, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";
import { AdminHeader, TableWrap, Th, Td, Tr, Spinner, EmptyRow, Modal, Field } from "@/components/admin/ui";
import { Calendar } from "@/components/ui/calendar";

const STATUSES = ["upcoming", "active", "completed", "cancelled"];
const toLocalInput = (d) => {
  const dt = new Date(d);
  const off = dt.getTimezoneOffset();
  return new Date(dt.getTime() - off * 60000).toISOString().slice(0, 16);
};
const dayKey = (d) => format(d, "yyyy-MM-dd");
const combineDayTime = (d, hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  const dt = new Date(d);
  dt.setHours(h || 0, m || 0, 0, 0);
  return dt;
};

const DEFAULT_POS_PTS = [25, 20, 16, 13, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 1, 1, 1, 1, 1];

const emptyForm = {
  name: "", description: "", game_type: "", tournament_type: "gaming", selected_stations: [],
  position_points: [...DEFAULT_POS_PTS],
  max_participants: 8, entry_fee: 0, prize_pool: 0,
  prize_pool_positions: [{ position: 1, amount: 0, description: "" }],
  days: [],
  daily_start_time: "18:00",
  daily_end_time: "21:00",
  registration_deadline: toLocalInput(Date.now() + 86400000), rules: "",
};

export default function AdminTournaments() {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    Promise.all([apiClient.get("/tournaments"), apiClient.get("/stations")])
      .then(([t, s]) => { setTournaments(t.data); setStations(s.data); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const toggleStation = (id) => setForm((f) => ({
    ...f, selected_stations: f.selected_stations.includes(id) ? f.selected_stations.filter((x) => x !== id) : [...f.selected_stations, id],
  }));

  const removeDay = (d) => setForm((f) => ({ ...f, days: f.days.filter((x) => dayKey(x) !== dayKey(d)) }));

  const create = async () => {
    if (!form.name || !form.game_type || form.selected_stations.length === 0) {
      toast.error("Name, game type and at least one station are required"); return;
    }
    if (form.days.length === 0) { toast.error("Select at least one competition day"); return; }
    const [sh, sm] = form.daily_start_time.split(":").map(Number);
    const [eh, em] = form.daily_end_time.split(":").map(Number);
    if (eh * 60 + em <= sh * 60 + sm) { toast.error("Daily end time must be after start time"); return; }
    const days = form.days.map((d) => ({
      start_datetime: combineDayTime(d, form.daily_start_time).toISOString(),
      end_datetime: combineDayTime(d, form.daily_end_time).toISOString(),
    }));
    setSaving(true);
    try {
      await apiClient.post("/admin/tournaments", {
        name: form.name, description: form.description, game_type: form.game_type,
        tournament_type: form.tournament_type,
        position_points: (form.position_points || DEFAULT_POS_PTS).map((n) => Number(n) || 0),
        selected_stations: form.selected_stations, max_participants: Number(form.max_participants),
        entry_fee: Number(form.entry_fee), prize_pool: Number(form.prize_pool),
        prize_pool_positions: (form.prize_pool_positions || []).filter((p) => Number(p.amount) > 0).map((p) => ({
          position: Number(p.position), amount: Number(p.amount), description: (p.description || "").trim(),
        })),
        days,
        registration_deadline: new Date(form.registration_deadline).toISOString(),
        rules: form.rules,
      });
      toast.success("Tournament created");
      setModalOpen(false); setForm(emptyForm); load();
    } catch (err) { toast.error(getErrorMessage(err, "Create failed")); } finally { setSaving(false); }
  };

  const changeStatus = async (id, status) => {
    try {
      await apiClient.put(`/admin/tournaments/${id}`, { status });
      setTournaments((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
      toast.success(`Status set to ${status}`);
    } catch (err) { toast.error(getErrorMessage(err, "Update failed")); }
  };

  const deleteTournament = async (t) => {
    const count = t.participants?.length || 0;
    const msg = count > 0
      ? `Delete "${t.name}"? This will remove the tournament, cancel bookings, and notify ${count} registered player${count === 1 ? "" : "s"}. This cannot be undone.`
      : `Delete "${t.name}"? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await apiClient.delete(`/admin/tournaments/${t.id}`);
      setTournaments((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Tournament deleted");
    } catch (err) { toast.error(getErrorMessage(err, "Delete failed")); }
  };

  if (loading) return <Spinner label="Loading tournaments" />;

  return (
    <div>
      <AdminHeader title="Tournaments" subtitle={`${tournaments.length} tournaments`}
        right={<button onClick={() => { setForm(emptyForm); setModalOpen(true); }} className="neon-btn px-5 py-2.5" data-testid="add-tournament"><Plus className="w-4 h-4" /> Create</button>} />

      <TableWrap>
        <thead><Tr><Th>Name</Th><Th>Game</Th><Th>Players</Th><Th>Prize</Th><Th>Days</Th><Th>Status</Th><Th></Th></Tr></thead>
        <tbody>
          {tournaments.length === 0 ? <EmptyRow colSpan={7} text="No tournaments yet" /> : tournaments.map((t) => {
            const dayCount = (t.days?.length || 0) || (t.start_datetime ? 1 : 0);
            return (
              <Tr key={t.id}>
                <Td className="text-white font-semibold" data-testid={`tournament-name-${t.id}`}>
                  <button onClick={() => navigate(`/admin/tournaments/${t.id}`)} className="text-white hover:text-cyan-400 transition-colors text-left" data-testid={`tournament-open-${t.id}`}>{t.name}</button>
                </Td>
                <Td>{t.game_type}</Td>
                <Td><span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5 text-slate-500" />{t.participants?.length || 0}/{t.max_participants}</span></Td>
                <Td><span style={{ color: "#00ff9d" }}><Award className="w-3.5 h-3.5 inline mb-0.5" />${t.prize_pool}</span></Td>
                <Td className="text-slate-300">
                  {t.start_datetime ? new Date(t.start_datetime).toLocaleDateString() : "—"}
                  {dayCount > 1 && <span className="ml-1 text-slate-500 text-xs">+{dayCount - 1} more</span>}
                </Td>
                <Td>
                  <select value={t.status} onChange={(e) => changeStatus(t.id, e.target.value)} data-testid={`tournament-status-${t.id}`}
                    className="bg-slate-900 border border-white/10 rounded-lg px-2 py-1 text-slate-200 capitalize">
                    {STATUSES.map((s) => <option key={s} value={s} className="bg-slate-900">{s}</option>)}
                  </select>
                </Td>
                <Td>
                  <div className="flex items-center gap-3">
                    <button onClick={() => navigate(`/admin/tournaments/${t.id}`)} className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-300 font-semibold" data-testid={`tournament-manage-${t.id}`}>Manage <ChevronRight className="w-4 h-4" /></button>
                    <button onClick={() => deleteTournament(t)} className="text-slate-500 hover:text-pink-400" title="Delete tournament" data-testid={`tournament-delete-${t.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </TableWrap>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Create Tournament" testId="tournament-modal">
        <Field label="Name"><input className="input-cyber" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tournament-form-name" /></Field>
        <Field label="Game Type"><input className="input-cyber" placeholder="e.g. Racing Championship" value={form.game_type} onChange={(e) => setForm({ ...form, game_type: e.target.value })} data-testid="tournament-form-game" /></Field>
        <Field label="Tournament Type">
          <select className="input-cyber" value={form.tournament_type} onChange={(e) => setForm({ ...form, tournament_type: e.target.value })} data-testid="tournament-form-type">
            <option value="time_attack">Time Attack (auto-scored by best lap)</option>
            <option value="racing">Racing (auto-scored by finish position)</option>
            <option value="gaming">Gaming (manual results)</option>
          </select>
        </Field>
        {form.tournament_type !== "gaming" && (
          <Field label="Points per finishing position (P1–P20)">
            <div className="grid grid-cols-5 gap-2" data-testid="tournament-form-points">
              {(form.position_points || DEFAULT_POS_PTS).map((val, idx) => (
                <div key={idx} className="flex flex-col items-center">
                  <span className="text-[10px] text-slate-500 font-bold">P{idx + 1}</span>
                  <input type="number" min="0" className="input-cyber text-center px-1 py-1 text-sm" value={val}
                    onChange={(e) => setForm((f) => ({ ...f, position_points: (f.position_points || DEFAULT_POS_PTS).map((x, i) => i === idx ? e.target.value : x) }))}
                    data-testid={`pos-pts-${idx + 1}`} />
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-2">Each day&apos;s finish awards these points; overall winner has the most points across all days.</p>
          </Field>
        )}
        <Field label="Description"><textarea className="input-cyber min-h-[60px] resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="tournament-form-desc" /></Field>
        <Field label="Stations">
          <div className="flex flex-wrap gap-2">
            {stations.map((s) => {
              const sel = form.selected_stations.includes(s.id);
              return <button key={s.id} type="button" onClick={() => toggleStation(s.id)}
                className="chip" data-testid={`tournament-station-${s.id}`}
                style={{ background: sel ? "rgba(0,229,255,0.15)" : "rgba(8,11,22,0.6)", border: `1px solid ${sel ? "rgba(0,229,255,0.6)" : "rgba(120,150,220,0.2)"}`, color: sel ? "#00e5ff" : "#94a3b8" }}>
                {s.name}
              </button>;
            })}
          </div>
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Max Players"><input type="number" min="2" className="input-cyber" value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: e.target.value })} data-testid="tournament-form-max" /></Field>
          <Field label="Entry Fee ($)"><input type="number" min="0" className="input-cyber" value={form.entry_fee} onChange={(e) => setForm({ ...form, entry_fee: e.target.value })} data-testid="tournament-form-fee" /></Field>
          <Field label="Prize Total ($)"><input type="number" min="0" className="input-cyber" value={form.prize_pool} onChange={(e) => setForm({ ...form, prize_pool: e.target.value })} data-testid="tournament-form-prize" /></Field>
        </div>

        <Field label="Prize Breakdown (per position)">
          <div className="space-y-2" data-testid="prize-positions">
            {(form.prize_pool_positions || []).map((p, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`prize-row-${idx}`}>
                <input type="number" min="1" placeholder="#" className="input-cyber col-span-2" value={p.position}
                  onChange={(e) => setForm((f) => ({ ...f, prize_pool_positions: f.prize_pool_positions.map((x, i) => i === idx ? { ...x, position: e.target.value } : x) }))} data-testid={`prize-position-${idx}`} />
                <input type="number" min="0" placeholder="Amount $" className="input-cyber col-span-3" value={p.amount}
                  onChange={(e) => setForm((f) => ({ ...f, prize_pool_positions: f.prize_pool_positions.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x) }))} data-testid={`prize-amount-${idx}`} />
                <input type="text" placeholder="Description (e.g. 1st place - cash + trophy)" className="input-cyber col-span-6" value={p.description}
                  onChange={(e) => setForm((f) => ({ ...f, prize_pool_positions: f.prize_pool_positions.map((x, i) => i === idx ? { ...x, description: e.target.value } : x) }))} data-testid={`prize-desc-${idx}`} />
                <button type="button" onClick={() => setForm((f) => ({ ...f, prize_pool_positions: f.prize_pool_positions.filter((_, i) => i !== idx) }))}
                  className="col-span-1 text-red-300 hover:text-red-400" data-testid={`prize-remove-${idx}`}><X className="w-4 h-4 mx-auto" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setForm((f) => ({ ...f, prize_pool_positions: [...(f.prize_pool_positions || []), { position: (f.prize_pool_positions?.length || 0) + 1, amount: 0, description: "" }] }))}
              className="neon-outline-btn px-3 py-1.5 text-xs" data-testid="add-prize-row">+ Add position</button>
            <p className="text-slate-500 text-xs">Rows with amount 0 are skipped. Position 1 = winner; higher = further down the podium.</p>
          </div>
        </Field>

        <Field label="Competition Days">
          <div className="space-y-2">
            <div className="rounded-xl glass border border-white/10 p-2 inline-block" data-testid="tournament-form-calendar">
              <Calendar mode="multiple" selected={form.days}
                onSelect={(dates) => setForm((f) => ({ ...f, days: (dates || []).slice().sort((a, b) => a - b) }))}
                initialFocus />
            </div>
            {form.days.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="tournament-form-day-chips">
                {form.days.map((d) => (
                  <span key={dayKey(d)} className="chip inline-flex items-center gap-1.5" data-testid={`tournament-form-day-${dayKey(d)}`}
                    style={{ background: "rgba(0,229,255,0.12)", border: "1px solid rgba(0,229,255,0.45)", color: "#00e5ff" }}>
                    {format(d, "EEE, MMM d")}
                    <button type="button" onClick={() => removeDay(d)} className="hover:text-white" data-testid={`tournament-form-day-remove-${dayKey(d)}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-slate-500 text-xs">Click a date on the calendar to add it; click again to remove.</p>
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Daily Start Time"><input type="time" className="input-cyber" value={form.daily_start_time} onChange={(e) => setForm({ ...form, daily_start_time: e.target.value })} data-testid="tournament-form-daily-start" /></Field>
          <Field label="Daily End Time"><input type="time" className="input-cyber" value={form.daily_end_time} onChange={(e) => setForm({ ...form, daily_end_time: e.target.value })} data-testid="tournament-form-daily-end" /></Field>
        </div>
        <p className="text-slate-500 text-xs -mt-2 mb-3">These times apply to every competition day.</p>

        <Field label="Registration Deadline"><input type="datetime-local" className="input-cyber" value={form.registration_deadline} onChange={(e) => setForm({ ...form, registration_deadline: e.target.value })} data-testid="tournament-form-deadline" /></Field>
        <Field label="Rules"><textarea className="input-cyber min-h-[60px] resize-none" value={form.rules} onChange={(e) => setForm({ ...form, rules: e.target.value })} data-testid="tournament-form-rules" /></Field>

        <button onClick={create} disabled={saving} className="neon-btn w-full py-3 mt-2" data-testid="tournament-form-save">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : "Create Tournament"}
        </button>
      </Modal>
    </div>
  );
}
